import React, { useState } from 'react';

const AUTH_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const GOOGLE_REDIRECT_EXAMPLE = `${AUTH_BASE_URL}/auth/google/callback`;
const FACEBOOK_REDIRECT_EXAMPLE = `${AUTH_BASE_URL}/auth/facebook/callback`;

function GuideSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={`setup-guide__section${open ? ' setup-guide__section--open' : ''}`}>
      <button
        type="button"
        className="setup-guide__section-toggle"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span className="setup-guide__section-title">{title}</span>
        <span className="setup-guide__chevron" aria-hidden="true">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="setup-guide__section-body">{children}</div>}
    </div>
  );
}

function OAuthSetupGuide() {
  const [guideOpen, setGuideOpen] = useState(true);

  return (
    <div className={`setup-guide${guideOpen ? ' setup-guide--open' : ''}`}>
      <button
        type="button"
        className="setup-guide__header"
        onClick={() => setGuideOpen((prev) => !prev)}
        aria-expanded={guideOpen}
      >
        <div>
          <h2 className="setup-guide__title">How to get OAuth credentials</h2>
          <p className="setup-guide__subtitle">
            Step-by-step instructions to obtain Google and Facebook OAuth details for this form.
          </p>
        </div>
        <span className="setup-guide__chevron setup-guide__chevron--lg" aria-hidden="true">
          {guideOpen ? '−' : '+'}
        </span>
      </button>

      {guideOpen && (
        <div className="setup-guide__body">
          <div className="setup-guide__field-map">
            <h3>What goes in each field</h3>
            <table className="setup-guide__table">
              <thead>
                <tr>
                  <th>Form field</th>
                  <th>Where to find it</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Google Client ID</td>
                  <td>Google Cloud Console → APIs &amp; Services → Credentials → OAuth 2.0 Client ID</td>
                </tr>
                <tr>
                  <td>Google Client Secret</td>
                  <td>Same credentials page — shown once when the client is created (or reset)</td>
                </tr>
                <tr>
                  <td>Google Redirect URL</td>
                  <td>
                    Your auth service callback URL — use{' '}
                    <code>{GOOGLE_REDIRECT_EXAMPLE}</code>
                  </td>
                </tr>
                <tr>
                  <td>Facebook App ID</td>
                  <td>Meta for Developers → Your App → Settings → Basic → App ID</td>
                </tr>
                <tr>
                  <td>Facebook App Secret</td>
                  <td>Same page — click <strong>Show</strong> next to App Secret</td>
                </tr>
                <tr>
                  <td>Facebook Redirect URL</td>
                  <td>
                    Your auth service callback URL — use{' '}
                    <code>{FACEBOOK_REDIRECT_EXAMPLE}</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <GuideSection title="Google OAuth — step by step" defaultOpen>
            <ol className="setup-guide__steps">
              <li>
                Open the{' '}
                <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer">
                  Google Cloud Console
                </a>{' '}
                and sign in with your Google account.
              </li>
              <li>
                Create a new project or select an existing one from the project dropdown at the top.
              </li>
              <li>
                Go to <strong>APIs &amp; Services → OAuth consent screen</strong>.
                <ul>
                  <li>Choose <strong>External</strong> (or Internal if using Google Workspace).</li>
                  <li>Fill in the app name, support email, and developer contact email.</li>
                  <li>Add scopes: <code>email</code> and <code>profile</code> (or the full URLs below).</li>
                  <li>Add test users if the app is still in <strong>Testing</strong> mode.</li>
                </ul>
              </li>
              <li>
                Go to <strong>APIs &amp; Services → Credentials</strong> and click{' '}
                <strong>Create Credentials → OAuth client ID</strong>.
              </li>
              <li>
                Select application type <strong>Web application</strong>.
                <ul>
                  <li>
                    <strong>Name:</strong> any label (e.g. &quot;Pappz Auth&quot;).
                  </li>
                  <li>
                    <strong>Authorized redirect URIs:</strong> add exactly:
                    <code className="setup-guide__code-block">{GOOGLE_REDIRECT_EXAMPLE}</code>
                    For production, also add your live auth domain (e.g.{' '}
                    <code>https://api.yourdomain.com/auth/google/callback</code>).
                  </li>
                </ul>
              </li>
              <li>
                Click <strong>Create</strong>. Copy the <strong>Client ID</strong> and{' '}
                <strong>Client secret</strong> — paste them into the Google fields on this page.
              </li>
              <li>
                Paste <code>{GOOGLE_REDIRECT_EXAMPLE}</code> into the <strong>Google Redirect URL</strong>{' '}
                field (must match the URI in Google Cloud exactly, including <code>http</code> vs{' '}
                <code>https</code> and trailing slashes).
              </li>
              <li>
                Click <strong>Save Configuration</strong>, then use <strong>Test Google Config</strong> to verify.
              </li>
            </ol>
            <p className="setup-guide__note">
              <strong>Scopes used by the auth service:</strong>{' '}
              <code>https://www.googleapis.com/auth/userinfo.email</code> and{' '}
              <code>https://www.googleapis.com/auth/userinfo.profile</code>
            </p>
          </GuideSection>

          <GuideSection title="Facebook OAuth — step by step">
            <ol className="setup-guide__steps">
              <li>
                Open{' '}
                <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer">
                  Meta for Developers
                </a>{' '}
                and sign in.
              </li>
              <li>
                Click <strong>My Apps → Create App</strong>. Choose use case{' '}
                <strong>Authenticate and request data from users with Facebook Login</strong> (or
                &quot;Other&quot; → Consumer).
              </li>
              <li>
                Enter an app name and contact email, then create the app.
              </li>
              <li>
                From the app dashboard, add the <strong>Facebook Login</strong> product if it is not
                already added.
              </li>
              <li>
                Go to <strong>Facebook Login → Settings</strong> (under Products in the left sidebar).
                <ul>
                  <li>
                    Under <strong>Valid OAuth Redirect URIs</strong>, add:
                    <code className="setup-guide__code-block">{FACEBOOK_REDIRECT_EXAMPLE}</code>
                    For production, also add your live callback URL.
                  </li>
                  <li>Save changes.</li>
                </ul>
              </li>
              <li>
                Go to <strong>Settings → Basic</strong>.
                <ul>
                  <li>Copy <strong>App ID</strong> → paste into <strong>Facebook App ID</strong> on this page.</li>
                  <li>Click <strong>Show</strong> next to <strong>App Secret</strong> → paste into{' '}
                    <strong>Facebook App Secret</strong>.</li>
                  <li>Add your app domain and privacy policy URL if required before going live.</li>
                </ul>
              </li>
              <li>
                Paste <code>{FACEBOOK_REDIRECT_EXAMPLE}</code> into the <strong>Facebook Redirect URL</strong>{' '}
                field on this page.
              </li>
              <li>
                Set the app to <strong>Live</strong> mode (toggle at the top) when ready for real users.
                In <strong>Development</strong> mode, only app admins/developers/testers can log in.
              </li>
              <li>
                Click <strong>Save Configuration</strong>, then use <strong>Test Facebook Config</strong> to verify.
              </li>
            </ol>
          </GuideSection>

          <GuideSection title="Important notes">
            <ul className="setup-guide__bullets">
              <li>
                <strong>Redirect URLs must match exactly</strong> in Google/Facebook consoles and in this
                form. A mismatch is the most common cause of OAuth errors.
              </li>
              <li>
                <strong>Secrets are not shown again</strong> after saving. Leave the secret fields blank
                when updating other settings to keep the existing secret.
              </li>
              <li>
                <strong>Product-specific config:</strong> select a product above to override the default
                OAuth settings for that product only. Leave as default for global settings shared by all
                products.
              </li>
              <li>
                <strong>Local development:</strong> if your auth service runs on a different port or host,
                update <code>REACT_APP_API_URL</code> in <code>.env</code> and use the matching callback
                URLs shown above in both the provider console and this form.
              </li>
              <li>
                <strong>Publishing:</strong> Google apps in Testing mode only allow listed test users.
                Facebook apps in Development mode only allow roles assigned in the app dashboard.
              </li>
            </ul>
          </GuideSection>
        </div>
      )}
    </div>
  );
}

export default OAuthSetupGuide;
